import {
  AppBase,
  baseFromDoc,
  FirestoreBaseDoc,
  makeDocConverter,
} from '../base/firestore-model-base'
import { Timestamp } from 'firebase/firestore'
import type { TownCompanyResponseStatus } from '../../utils/town-company-status'
import { normalizePostalCodeForStorage } from '../../utils/postal-code'

/**
 * 事業所アンケート回答（固定質問に対応するプロパティ）
 * ResponsesTab の列構成に合わせた固定プロパティ。
 */

/** Firestoreに保存される形（snake_case / Timestamp） */
export type CompanySurveyAnswerDoc = FirestoreBaseDoc & {
  /** 管理ID */
  management_id?: string | null
  /** 事業所名 */
  company_name?: string | null
  /** 郵便番号 */
  postal_code?: string | null
  /** 住所 */
  address?: string | null
  /** 電話番号 */
  phone?: string | null
  /** 郵送回 */
  request_number?: string | null
  /** 業種 */
  business_type?: string | null
  /** 担当者氏名 */
  contact_name?: string | null
  /** フリガナ */
  contact_name_kana?: string | null
  /** メールアドレス */
  email?: string | null
  /** 受け入れ可否 */
  acceptance_status?: TownCompanyResponseStatus | null
  /** 受け入れ不可理由 */
  rejection_reason?: string | null
  /** 受け入れ不可理由「その他」の内容 */
  rejection_reason_other?: string | null
  /** 実体験先が異なるか */
  different_experience_site?: boolean | null
  /** 体験先の事業所名 */
  actual_company_name?: string | null
  /** 体験先の郵便番号 */
  actual_postal_code?: string | null
  /** 体験先の住所 */
  actual_address?: string | null
  /** 事前の腸内細菌検査の実施要否 */
  bacteria_test_requirement?: string | null
  /** 特別支援学級在籍生徒の受け入れ可否 */
  special_support_acceptance?: string | null
  /** 生徒による事前連絡・訪問への対応可否 */
  pre_contact_visit_support?: string | null
  /** 事前連絡・訪問の対応可能日程 */
  pre_contact_visit_support_schedule?: string | null
  /** 受け入れ可能日（複数） */
  accepted_dates?: string[] | null
  /** 受け入れ可能日数 */
  accepted_days_count?: number | null
  /** 日付ごとの受け入れ設定 */
  per_date_schedules?: Record<string, CompanySurveyAnswerPerDateScheduleDoc> | null
  /** 開始時間 */
  start_time?: string | null
  /** 終了時間 */
  end_time?: string | null
  /** 受け入れ最大人数 */
  max_capacity?: number | null
  /** 訪問時の服装 */
  attire_visit?: string | null
  /** 体験時の服装 */
  attire_experience?: string | null
  /** 昼食についての連絡事項 */
  lunch_notes?: string | null
  /** 持ち物 */
  items_to_bring?: string | null
  /** 備考 */
  remarks?: string | null
  /** その他の質問（文字列の配列） */
  others?: string[] | null
  /** 回答提出日時 */
  submitted_at?: Timestamp | null
}

export type CompanySurveyAnswerPerDateScheduleDoc = {
  start_time?: string | null
  end_time?: string | null
  max_capacity?: number | null
}

/** アプリで使う形（camelCase / Date） */
export type CompanySurveyAnswer = AppBase & {
  managementId?: string
  companyName?: string
  postalCode?: string
  address?: string
  phone?: string
  requestNumber?: string
  businessType?: string
  contactName?: string
  contactNameKana?: string
  email?: string
  acceptanceStatus?: TownCompanyResponseStatus
  rejectionReason?: string
  rejectionReasonOther?: string
  differentExperienceSite?: boolean
  actualCompanyName?: string
  actualPostalCode?: string
  actualAddress?: string
  bacteriaTestRequirement?: string
  specialSupportAcceptance?: string
  preContactVisitSupport?: string
  preContactVisitSupportSchedule?: string
  acceptedDates?: string[]
  acceptedDaysCount?: number
  perDateSchedules?: Record<string, CompanySurveyAnswerPerDateSchedule>
  startTime?: string
  endTime?: string
  maxCapacity?: number
  attireVisit?: string
  attireExperience?: string
  lunchNotes?: string
  itemsToBring?: string
  remarks?: string
  /** その他の質問（文字列の配列） */
  others?: string[]
  submitted_at?: Date
}

export type CompanySurveyAnswerPerDateSchedule = {
  startTime?: string
  endTime?: string
  maxCapacity?: number
}

/** 回答保存時に渡すペイロード（camelCase・App の answer 部分のみ） */
export type CompanySurveyAnswerPayload = Partial<Omit<CompanySurveyAnswer, keyof AppBase>>

/** Payload を Doc のフィールドに変換（snake_case） */
export function companySurveyAnswerPayloadToDoc(data: CompanySurveyAnswerPayload): Partial<CompanySurveyAnswerDoc> {
  const hasDifferentExperienceSite = data.differentExperienceSite === true
  const isRejected = data.acceptanceStatus === 'rejected'
  const perDateSchedules = data.perDateSchedules
    ? Object.fromEntries(
      Object.entries(data.perDateSchedules).map(([date, schedule]) => [
        date,
        {
          start_time: schedule?.startTime ?? null,
          end_time: schedule?.endTime ?? null,
          max_capacity: schedule?.maxCapacity ?? null,
        } satisfies CompanySurveyAnswerPerDateScheduleDoc,
      ]),
    )
    : null

  return {
    management_id: data.managementId ?? null,
    company_name: data.companyName ?? null,
    postal_code:
      data.postalCode != null && String(data.postalCode).trim() !== ''
        ? normalizePostalCodeForStorage(String(data.postalCode))
        : null,
    address: data.address ?? null,
    phone: data.phone ?? null,
    request_number: data.requestNumber ?? null,
    business_type: data.businessType ?? null,
    contact_name: data.contactName ?? null,
    contact_name_kana: data.contactNameKana ?? null,
    email: data.email ?? null,
    acceptance_status: data.acceptanceStatus ?? null,
    rejection_reason: isRejected ? (data.rejectionReason ?? null) : null,
    rejection_reason_other:
      isRejected && data.rejectionReason === 'other' ? (data.rejectionReasonOther ?? null) : null,
    different_experience_site: hasDifferentExperienceSite,
    actual_company_name: hasDifferentExperienceSite ? (data.actualCompanyName ?? null) : null,
    actual_postal_code:
      hasDifferentExperienceSite &&
      data.actualPostalCode != null &&
      String(data.actualPostalCode).trim() !== ''
        ? normalizePostalCodeForStorage(String(data.actualPostalCode))
        : null,
    actual_address: hasDifferentExperienceSite ? (data.actualAddress ?? null) : null,
    bacteria_test_requirement: data.bacteriaTestRequirement ?? null,
    special_support_acceptance: data.specialSupportAcceptance ?? null,
    pre_contact_visit_support: data.preContactVisitSupport ?? null,
    pre_contact_visit_support_schedule: data.preContactVisitSupportSchedule ?? null,
    accepted_dates: data.acceptedDates ?? null,
    accepted_days_count: data.acceptedDaysCount ?? null,
    per_date_schedules: perDateSchedules,
    start_time: data.startTime ?? null,
    end_time: data.endTime ?? null,
    max_capacity: data.maxCapacity ?? null,
    attire_visit: data.attireVisit ?? null,
    attire_experience: data.attireExperience ?? null,
    lunch_notes: data.lunchNotes ?? null,
    items_to_bring: data.itemsToBring ?? null,
    remarks: data.remarks ?? null,
    others: data.others ?? null,
  }
}

export const companySurveyAnswerDocConverter = makeDocConverter<CompanySurveyAnswerDoc>()

export function toCompanySurveyAnswer(id: string, doc: CompanySurveyAnswerDoc, path: string): CompanySurveyAnswer {
  const raw = doc.submitted_at
  const submittedAt =
    raw != null && typeof raw === 'object' && 'toDate' in raw && typeof (raw as Timestamp).toDate === 'function'
      ? (raw as Timestamp).toDate()
      : raw instanceof Date
        ? raw
        : undefined

  const perDateSchedules = doc.per_date_schedules
    ? Object.fromEntries(
      Object.entries(doc.per_date_schedules).map(([date, schedule]) => [
        date,
        {
          startTime: schedule?.start_time ?? undefined,
          endTime: schedule?.end_time ?? undefined,
          maxCapacity: schedule?.max_capacity ?? undefined,
        } satisfies CompanySurveyAnswerPerDateSchedule,
      ]),
    )
    : undefined

  return {
    ...baseFromDoc(id, doc, path),
    managementId: doc.management_id ?? undefined,
    companyName: doc.company_name ?? undefined,
    postalCode: doc.postal_code ?? undefined,
    address: doc.address ?? undefined,
    phone: doc.phone ?? undefined,
    requestNumber: doc.request_number ?? undefined,
    businessType: doc.business_type ?? undefined,
    contactName: doc.contact_name ?? undefined,
    contactNameKana: doc.contact_name_kana ?? undefined,
    email: doc.email ?? undefined,
    acceptanceStatus: doc.acceptance_status ?? undefined,
    rejectionReason: doc.rejection_reason ?? undefined,
    rejectionReasonOther: doc.rejection_reason_other ?? undefined,
    differentExperienceSite: doc.different_experience_site ?? undefined,
    actualCompanyName: doc.actual_company_name ?? undefined,
    actualPostalCode: doc.actual_postal_code ?? undefined,
    actualAddress: doc.actual_address ?? undefined,
    bacteriaTestRequirement: doc.bacteria_test_requirement ?? undefined,
    specialSupportAcceptance: doc.special_support_acceptance ?? undefined,
    preContactVisitSupport: doc.pre_contact_visit_support ?? undefined,
    preContactVisitSupportSchedule: doc.pre_contact_visit_support_schedule ?? undefined,
    acceptedDates: doc.accepted_dates ?? undefined,
    acceptedDaysCount: doc.accepted_days_count ?? undefined,
    perDateSchedules,
    startTime: doc.start_time ?? undefined,
    endTime: doc.end_time ?? undefined,
    maxCapacity: doc.max_capacity ?? undefined,
    attireVisit: doc.attire_visit ?? undefined,
    attireExperience: doc.attire_experience ?? undefined,
    lunchNotes: doc.lunch_notes ?? undefined,
    itemsToBring: doc.items_to_bring ?? undefined,
    remarks: doc.remarks ?? undefined,
    others: doc.others ?? undefined,
    submitted_at: submittedAt,
  }
}
